import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SigningRequestWizard } from '@features/signing-requests/SigningRequestWizard';
import { dsEnvelopeService } from '@services/dsEnvelopeService';
import { useAuthStore } from '@stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { useTranslation } from '@hooks/useTranslation';
import type { EnvelopeStatus } from '@workpay/ds-shared/types/envelope';

interface EditSearch {
  retriggerMode: 'reset_all' | 'continue';
  startStep?: number;
}

const EDITABLE_STATUSES: EnvelopeStatus[] = [
  'DRAFT',
  'REVIEW_COMPLETED',
  'SENT',
  'VIEWED',
  'SIGNING_IN_PROGRESS',
  'REVISED',
  'PENDING_REVIEW',
  'UNDER_NEGOTIATION',
];

// Statuses that share DRAFT's write semantics (backend `requireDraft` accepts both).
const DRAFT_LIKE_STATUSES: EnvelopeStatus[] = ['DRAFT', 'REVIEW_COMPLETED'];

export const Route = createFileRoute('/w/signing-requests/$requestId_/edit')({
  staticData: {
    hideShell: true,
    breadcrumb: 'Edit Signing Request',
    breadcrumbKey: 'K_BREADCRUMB_EDIT_SIGNING_REQUEST',
  },
  validateSearch: (search: Record<string, unknown>): EditSearch => ({
    retriggerMode: (search.retriggerMode as 'reset_all' | 'continue') ?? 'reset_all',
    startStep: search.startStep != null ? Number(search.startStep) : undefined,
  }),
  component: WorkpayEditSigningRequestPage,
});

function WorkpayEditSigningRequestPage() {
  const { requestId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { t } = useTranslation();

  const { data: signingRequest, isLoading } = useQuery({
    queryKey: ['dsEnvelope', requestId],
    queryFn: () => dsEnvelopeService.getById(requestId),
    enabled: !!requestId,
  });

  useEffect(() => {
    if (!signingRequest) return;
    if (!EDITABLE_STATUSES.includes(signingRequest.status)) {
      addNotification({
        variant: 'critical',
        title: t(
          'K_DS_NOTIF_SIGNING_REQUEST_NOT_EDITABLE',
          'This signing request cannot be edited in its current state',
        ),
      });
      navigate({ to: '/w/signing-requests' });
    }
  }, [signingRequest, navigate, addNotification, t]);

  if (!hasPermission('ENVELOPES_UPDATE') && !hasPermission('ENVELOPES_REVISE')) {
    return null;
  }

  if (isLoading || !signingRequest) {
    return null;
  }

  if (!EDITABLE_STATUSES.includes(signingRequest.status)) {
    return null;
  }

  const isDraftLike = DRAFT_LIKE_STATUSES.includes(signingRequest.status);
  const requiredPerm = isDraftLike ? 'ENVELOPES_UPDATE' : 'ENVELOPES_REVISE';
  if (!hasPermission(requiredPerm)) {
    return null;
  }

  // REVIEW_COMPLETED entry defaults to Step 4 (Place Fields) when the URL
  // doesn't carry an explicit `?startStep=` param. The wizard's hasUserSteppedBack
  // gate hides Steps 0–3 until the user accepts the back-button warning.
  const resolvedStartStep =
    search.startStep ?? (signingRequest.status === 'REVIEW_COMPLETED' ? 4 : undefined);

  return (
    <SigningRequestWizard
      routeTo="/w/signing-requests"
      editRequestId={requestId}
      mode={isDraftLike ? 'continue' : 'edit'}
      initialData={signingRequest}
      retriggerMode={search.retriggerMode}
      startStep={resolvedStartStep}
    />
  );
}
